import * as THREE from 'three';
import { DAY_LENGTH } from '../constants';
import { lerp } from '../utils/math';

export class Sky {
  private dome: THREE.Mesh;
  private sunLight: THREE.DirectionalLight;
  private ambientLight: THREE.AmbientLight;
  private sunMesh: THREE.Mesh;
  private moonMesh: THREE.Mesh;
  private timeOfDay = 0.25; // Start at morning
  private scene: THREE.Scene;

  constructor(scene: THREE.Scene, sunLight: THREE.DirectionalLight, ambientLight: THREE.AmbientLight) {
    this.scene = scene;
    this.sunLight = sunLight;
    this.ambientLight = ambientLight;

    // Sky dome
    const skyGeo = new THREE.SphereGeometry(500, 16, 16);
    const skyMat = new THREE.ShaderMaterial({
      uniforms: {
        topColor: { value: new THREE.Color(0x0077ff) },
        bottomColor: { value: new THREE.Color(0x87ceeb) },
        offset: { value: 20 },
        exponent: { value: 0.6 },
      },
      vertexShader: `
        varying vec3 vWorldPosition;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        uniform float offset;
        uniform float exponent;
        varying vec3 vWorldPosition;
        void main() {
          float h = normalize(vWorldPosition + offset).y;
          gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)), 1.0);
        }
      `,
      side: THREE.BackSide,
      depthWrite: false,
    });
    this.dome = new THREE.Mesh(skyGeo, skyMat);
    this.dome.renderOrder = -1;
    scene.add(this.dome);

    // Sun
    const sunGeo = new THREE.CircleGeometry(20, 16);
    const sunMat = new THREE.MeshBasicMaterial({ color: 0xffee88, side: THREE.DoubleSide });
    this.sunMesh = new THREE.Mesh(sunGeo, sunMat);
    scene.add(this.sunMesh);

    // Moon
    const moonGeo = new THREE.CircleGeometry(15, 16);
    const moonMat = new THREE.MeshBasicMaterial({ color: 0xccccdd, side: THREE.DoubleSide });
    this.moonMesh = new THREE.Mesh(moonGeo, moonMat);
    scene.add(this.moonMesh);
  }

  update(dt: number, cameraPosition: THREE.Vector3): void {
    this.timeOfDay = (this.timeOfDay + dt / DAY_LENGTH) % 1;

    const angle = this.timeOfDay * Math.PI * 2;
    const sunX = Math.cos(angle) * 300;
    const sunY = Math.sin(angle) * 300;

    // Move dome with camera
    this.dome.position.copy(cameraPosition);

    // Sun position
    this.sunMesh.position.set(
      cameraPosition.x + sunX,
      cameraPosition.y + sunY,
      cameraPosition.z,
    );
    this.sunMesh.lookAt(cameraPosition);

    // Moon opposite to sun
    this.moonMesh.position.set(
      cameraPosition.x - sunX,
      cameraPosition.y - sunY,
      cameraPosition.z,
    );
    this.moonMesh.lookAt(cameraPosition);

    // Sun/moon visibility
    this.sunMesh.visible = sunY > -50;
    this.moonMesh.visible = sunY < 50;

    // Update directional light
    this.sunLight.position.set(sunX, Math.max(sunY, 10), 0).normalize();

    // Day/night brightness
    const sunHeight = Math.sin(angle);
    const dayBrightness = Math.max(0, Math.min(1, sunHeight * 2 + 0.3));

    this.sunLight.intensity = lerp(0.05, 0.8, dayBrightness);
    this.ambientLight.intensity = lerp(0.15, 0.6, dayBrightness);

    // Sky colors
    const mat = this.dome.material as THREE.ShaderMaterial;
    const topDay = new THREE.Color(0x0077ff);
    const topNight = new THREE.Color(0x000022);
    const bottomDay = new THREE.Color(0x87ceeb);
    const bottomNight = new THREE.Color(0x000011);

    const topSunset = new THREE.Color(0xff5500);
    const bottomSunset = new THREE.Color(0xff8844);

    const sunsetFactor = Math.max(0, 1 - Math.abs(sunHeight) * 5);

    const topColor = topDay.clone().lerp(topNight, 1 - dayBrightness).lerp(topSunset, sunsetFactor * 0.5);
    const bottomColor = bottomDay.clone().lerp(bottomNight, 1 - dayBrightness).lerp(bottomSunset, sunsetFactor * 0.5);

    mat.uniforms.topColor.value.copy(topColor);
    mat.uniforms.bottomColor.value.copy(bottomColor);

    // Update fog color
    if (this.scene.fog) {
      (this.scene.fog as THREE.Fog).color.copy(bottomColor);
    }

    // Update clear color
    this.scene.background = bottomColor;
  }

  get time(): number {
    return this.timeOfDay;
  }

  setTime(t: number): void {
    this.timeOfDay = t % 1;
  }

  dispose(): void {
    this.scene.remove(this.dome);
    this.scene.remove(this.sunMesh);
    this.scene.remove(this.moonMesh);
    (this.dome.material as THREE.Material).dispose();
    this.dome.geometry.dispose();
    (this.sunMesh.material as THREE.Material).dispose();
    this.sunMesh.geometry.dispose();
    (this.moonMesh.material as THREE.Material).dispose();
    this.moonMesh.geometry.dispose();
  }
}
